/**
 * Geometry utility functions for rotation calculations and positioning
 */

import * as THREE from 'three';

/**
 * Get the world Y rotation of a mesh
 * @param {THREE.Object3D} mesh - The mesh to get rotation from
 * @returns {number} The Y rotation in radians
 */
// Scratch — called per module per frame from collision, snapping and dimensioning.
const _yawQuat = new THREE.Quaternion();
const _yawEuler = new THREE.Euler();

export function getWorldYRotation(mesh) {
  mesh.getWorldQuaternion(_yawQuat);
  return _yawEuler.setFromQuaternion(_yawQuat, 'YXZ').y;
}

/** An angle in radians wrapped into (−π, π]; exact for whole turns. */
export function wrapAngle(a) {
  a %= 2 * Math.PI;
  return a > Math.PI ? a - 2 * Math.PI : a <= -Math.PI ? a + 2 * Math.PI : a;
}

const _obbRadius = (b, lx, lz) =>
  b.hx * Math.abs(lx * b.cos - lz * b.sin) + b.hz * Math.abs(lx * b.sin + lz * b.cos);

/** SAT gap (m) between XZ boxes {cx, cz, cos, sin, hx, hz}, negative on overlap; local X=(cos,−sin), Z=(sin,cos). */
export function orientedXZGap(a, b) {
  const dx = b.cx - a.cx, dz = b.cz - a.cz;
  let gap = -Infinity;
  for (let i = 0; i < 4; i++) {
    const box = i < 2 ? a : b;
    const lx = i % 2 === 0 ? box.cos : box.sin;
    const lz = i % 2 === 0 ? -box.sin : box.cos;
    gap = Math.max(gap, Math.abs(dx * lx + dz * lz) - _obbRadius(a, lx, lz) - _obbRadius(b, lx, lz));
  }
  return gap;
}

/** Penetration depth (m) of two oriented XZ boxes: the minimum overlap, or 0 when separated. */
export function orientedXZPenetration(a, b) {
  return Math.max(0, -orientedXZGap(a, b));
}

/**
 * Check if both furniture items are modules (for module-to-module connections)
 * @param {object} furniture1 - First furniture element
 * @param {object} furniture2 - Second furniture element
 * @returns {boolean} True if both are modules
 */
export function isModuleToModuleConnection(furniture1, furniture2) {
  const furniture1IsModule = furniture1.furnitureData?.componentType === "module";
  const furniture2IsModule = furniture2.furnitureData?.componentType === "module";
  return furniture1IsModule && furniture2IsModule;
}

/**
 * Apply the composite-group transform for a given variant onto a mesh tree.
 * Used when rendering at rest pose (catalog thumbnails, fresh ghost preview):
 * the cached GLTF source has the embedded composite group at its authored rest
 * location, but spawned FurnitureElements always start at variant 0's offset.
 * Mutates `mesh` in place.
 *
 * @param {THREE.Object3D} mesh - root to traverse (typically a clone)
 * @param {object} furnitureData - catalog entry with properties.compositeGroupName + variants
 * @param {number} [variantIndex=0]
 * @returns {boolean} true if a composite group was found and updated
 */
export function applyVariantCompositeTransform(mesh, furnitureData, variantIndex = 0) {
  const props = furnitureData?.properties;
  const groupName = props?.compositeGroupName;
  if (!mesh || !groupName) return false;
  const variant = props.variants?.[variantIndex];
  if (!variant) return false;

  const offset = variant.compositeOffset || { x: 0, y: 0, z: 0 };
  const rotY = variant.compositeRotationY ?? 0;
  const visible = variant.compositeVisible !== false;

  let found = false;
  mesh.traverse((node) => {
    if (node.name === groupName) {
      node.visible = visible;
      if (visible) {
        node.position.set(offset.x, offset.y, offset.z);
        node.rotation.set(0, THREE.MathUtils.degToRad(rotY), 0);
      }
      found = true;
    }
  });
  return found;
}

/**
 * Resolve the active attachment-point list for a furniture entry at the given variant.
 * Per-variant APs take precedence over property-level APs (legacy fallback).
 * @param {object} furnitureData
 * @param {number} [variantIndex=0]
 * @returns {Array}
 */
export function getActiveAttachmentPoints(furnitureData, variantIndex = 0) {
  const props = furnitureData?.properties;
  return props?.variants?.[variantIndex]?.attachmentPoints
    ?? props?.attachmentPoints
    ?? [];
}

/**
 * Apply a variant's `nodeAnimations` overrides (position / rotation / visibility)
 * to named child nodes inside a mesh tree. Assumes `mesh` is a fresh clone whose
 * named nodes are at bind pose — variants without an entry leave their nodes
 * untouched, which matches the convention used by FurnitureElement at runtime.
 * Mutates `mesh` in place.
 *
 * @param {THREE.Object3D} mesh - root to traverse (typically a clone)
 * @param {object} furnitureData - catalog entry with properties.variants
 * @param {number} [variantIndex=0]
 * @returns {boolean} true if any override was applied
 */
export function applyVariantNodeAnimations(mesh, furnitureData, variantIndex = 0) {
  const anims = furnitureData?.properties?.variants?.[variantIndex]?.nodeAnimations;
  if (!mesh || !anims?.length) return false;

  const wanted = new Set();
  for (const a of anims) if (a.node) wanted.add(a.node);
  if (wanted.size === 0) return false;

  const nodes = new Map();
  mesh.traverse((c) => {
    if (c.name && wanted.has(c.name) && !nodes.has(c.name)) nodes.set(c.name, c);
  });

  let applied = false;
  for (const a of anims) {
    const node = nodes.get(a.node);
    if (!node) continue;
    if (a.position) {
      node.position.set(a.position.x ?? 0, a.position.y ?? 0, a.position.z ?? 0);
    }
    if (a.rotation) {
      node.rotation.set(
        THREE.MathUtils.degToRad(a.rotation.x ?? 0),
        THREE.MathUtils.degToRad(a.rotation.y ?? 0),
        THREE.MathUtils.degToRad(a.rotation.z ?? 0),
      );
    }
    if (a.visible !== undefined) node.visible = a.visible !== false;
    applied = true;
  }
  return applied;
}

/**
 * Apply both authoring-stage display transforms — mirror and the variant-0
 * composite group — to a freshly cloned mesh tree. Used when rendering at rest
 * pose (cart thumbnails, layout thumbnails) where the source clone still sits
 * at the GLTF's authored pose.
 * @param {THREE.Object3D} mesh - root to mutate (typically a clone)
 * @param {object} furnitureData - catalog entry
 * @param {number} [variantIndex=0]
 * @returns {THREE.Object3D} the same mesh, for chaining
 */
export function applyDisplayTransforms(mesh, furnitureData, variantIndex = 0) {
  const props = furnitureData?.properties;
  if (!mesh || !props) return mesh;
  if (props.mirror) {
    applyMirror(mesh, props.mirror);
  }
  if (props.compositeGroupName) {
    applyVariantCompositeTransform(mesh, furnitureData, variantIndex);
  }
  applyVariantNodeAnimations(mesh, furnitureData, variantIndex);
  return mesh;
}

const _mirrorBox = new THREE.Box3();
const _mirrorCenterBefore = new THREE.Vector3();
const _mirrorCenterAfter = new THREE.Vector3();
const _mirrorRootPos = new THREE.Vector3();
const _mirrorRootQuat = new THREE.Quaternion();
const _mirrorRootScale = new THREE.Vector3();

/**
 * Mirror a furniture mesh tree along one of its *local* node axes, *in place*:
 * negate each child node's scale on that axis, then translate the children so the
 * whole object's bounding-box center is unchanged.
 *
 * Two reasons the axis is configurable and the center is preserved:
 *  - A plain `scale.x *= -1` reflects about the root's X=0 plane, which teleports
 *    nodes whose footprint is off-center along the world axis their local axis maps
 *    to. Re-centering after the flip makes the mirror placement-invariant.
 *  - Each model's node rotation sends its local X to a *different* world axis, so a
 *    hardcoded local-X flip mirrors different real-world axes per model. The author
 *    picks the axis that flips the piece's actual handedness via the `mirror`
 *    catalog property, whose value IS the axis (e.g. choice-p14 = 'x', choice-p5 = 'z').
 *
 * The center is measured with the root temporarily reset to identity so the
 * before/after delta lives in the root's local space (where child.position lives),
 * independent of where the element is currently placed/rotated in the scene.
 * @param {THREE.Object3D} root - mesh tree to mirror (mutated in place)
 * @param {'x'|'y'|'z'} [axis='x'] - local node axis to negate
 * @returns {THREE.Object3D} the same root, for chaining
 */
export function applyMirror(root, axis = 'x') {
  if (!root || !root.children?.length) return root;

  // Stash and neutralize the root transform so bbox math is in root-local space.
  _mirrorRootPos.copy(root.position);
  _mirrorRootQuat.copy(root.quaternion);
  _mirrorRootScale.copy(root.scale);
  root.position.set(0, 0, 0);
  root.quaternion.identity();
  root.scale.set(1, 1, 1);
  root.updateMatrixWorld(true);

  _mirrorBox.setFromObject(root);
  _mirrorBox.getCenter(_mirrorCenterBefore);

  root.children.forEach((child) => { child.scale[axis] *= -1; });
  root.updateMatrixWorld(true);

  _mirrorBox.setFromObject(root);
  _mirrorBox.getCenter(_mirrorCenterAfter);

  // Shift every child equally to cancel the center drift the flip introduced.
  const dx = _mirrorCenterAfter.x - _mirrorCenterBefore.x;
  const dy = _mirrorCenterAfter.y - _mirrorCenterBefore.y;
  const dz = _mirrorCenterAfter.z - _mirrorCenterBefore.z;
  root.children.forEach((child) => {
    child.position.x -= dx;
    child.position.y -= dy;
    child.position.z -= dz;
  });

  // Restore the root's original transform.
  root.position.copy(_mirrorRootPos);
  root.quaternion.copy(_mirrorRootQuat);
  root.scale.copy(_mirrorRootScale);
  root.updateMatrixWorld(true);
  return root;
}

const _scratchQuat = new THREE.Quaternion();
const _scratchEuler = new THREE.Euler();
const _scratchVec = new THREE.Vector3();
const _zeroOffset = { x: 0, y: 0, z: 0 };

/**
 * Invoke `fn(cx, cy, cz, hw, hh, hd)` for each world-space collision sub-box of a
 * furniture element, inflated by `padding`. Handles multi-box L-shapes and falls back
 * to `boundingBox`; reads world transforms so reparented components resolve correctly.
 * Zero-allocation, so it is safe to call every frame.
 */
export function forEachWorldCollisionBox(element, padding, fn) {
  const mesh = element.getMesh?.();
  if (!mesh) return;
  mesh.getWorldPosition(_scratchVec);
  mesh.getWorldQuaternion(_scratchQuat);
  const rot = _scratchEuler.setFromQuaternion(_scratchQuat, 'YXZ').y;
  const cos = Math.cos(rot), sin = Math.sin(rot);
  const absCos = Math.abs(cos), absSin = Math.abs(sin);
  const { x: px, y: py, z: pz } = _scratchVec;

  const cbs = element.collisionBoxes;
  const hasBoxes = cbs?.length > 0;
  const count = hasBoxes ? cbs.length : (element.boundingBox ? 1 : 0);
  for (let i = 0; i < count; i++) {
    const size = hasBoxes ? cbs[i].size : element.boundingBox.size;
    const off = (hasBoxes ? cbs[i].offset : element.boundingBox.localCenterOffset) ?? _zeroOffset;
    fn(
      px + off.x * cos + off.z * sin,        // cx
      py + (off.y ?? 0),                     // cy
      pz - off.x * sin + off.z * cos,        // cz
      (size.x * absCos + size.z * absSin) / 2 + padding, // hw
      size.y / 2 + padding,                  // hh
      (size.x * absSin + size.z * absCos) / 2 + padding, // hd
    );
  }
}

/**
 * Test whether a world-space XZ point lies inside a furniture element's body
 * (collisionBoxes-aware). For multi-box / L-shaped pieces, tests each sub-box
 * individually — the union AABB would create false positives in concave regions.
 * Falls back to the element's `boundingBox` AABB when no collisionBoxes exist.
 *
 * @param {{collisionBoxes?: Array, boundingBox?: object, getMesh: () => THREE.Object3D}} furniture
 * @param {number} x - World X
 * @param {number} z - World Z
 * @param {number} [margin=0]
 * @returns {boolean}
 */
export function isPointInsideFurnitureXZ(furniture, x, z, margin = 0) {
  if (furniture.collisionBoxes?.length > 0) {
    const mesh = furniture.getMesh();
    if (!mesh) return false;
    mesh.getWorldQuaternion(_scratchQuat);
    const rotY = _scratchEuler.setFromQuaternion(_scratchQuat, 'YXZ').y;
    const cosR = Math.cos(rotY), sinR = Math.sin(rotY);
    const absCos = Math.abs(cosR), absSin = Math.abs(sinR);
    const pos = mesh.position;
    return furniture.collisionBoxes.some((cb) => {
      const cx = pos.x + cb.offset.x * cosR + cb.offset.z * sinR;
      const cz = pos.z - cb.offset.x * sinR + cb.offset.z * cosR;
      const hw = (cb.size.x * absCos + cb.size.z * absSin) / 2 + margin;
      const hd = (cb.size.x * absSin + cb.size.z * absCos) / 2 + margin;
      return x >= cx - hw && x <= cx + hw && z >= cz - hd && z <= cz + hd;
    });
  }
  const bb = furniture.boundingBox;
  if (!bb) return false;
  return (
    x >= bb.min.x - margin && x <= bb.max.x + margin &&
    z >= bb.min.z - margin && z <= bb.max.z + margin
  );
}

/**
 * Resolve the active authored collision spec for a variant.
 * Returns one of:
 *   { boxes: [{size,offset},...] }  multi-box
 *   { box:  {size,offset} }         single-box
 *   null                            no authored data → caller falls back to mesh AABB
 *
 * Variant entries take precedence over property-level fallbacks.
 * @param {object} properties - furnitureData.properties
 * @param {number} [variantIndex=0]
 */
export function resolveCollisionSpec(properties, variantIndex = 0) {
  if (!properties) return null;
  const variant = properties.variants?.[variantIndex];
  if (variant) {
    if (variant.collisionBoxes?.length > 0) return { boxes: variant.collisionBoxes };
    if (variant.collisionBox?.size)         return { box: variant.collisionBox };
  }
  if (properties.collisionBoxes?.length > 0) return { boxes: properties.collisionBoxes };
  if (properties.collisionBox?.size)         return { box: properties.collisionBox };
  return null;
}

/**
 * Plan-view area of a catalog item's authored collision spec, for sorting a grid by size.
 * Read from the data rather than the mesh so the order is known before any model loads.
 * Multi-box (L-shaped) pieces are measured by the union of their boxes, which is the
 * footprint the piece occupies. Returns null when the item authors no collision data.
 * @param {object} furnitureData - a `collections[].items[]` entry
 */
export function catalogFootprintArea(furnitureData) {
  const spec = resolveCollisionSpec(furnitureData?.properties);
  const boxes = spec?.boxes || (spec?.box ? [spec.box] : null);
  const u = boxes ? collisionBoxesUnion(boxes) : null;
  return u ? (u.maxX - u.minX) * (u.maxZ - u.minZ) : null;
}

/**
 * Axis-aligned union of authored collision boxes (`{size, offset?}`, mesh-local) after the
 * piece turns by `rotation` about Y — offsets rotate, each box's extents widen to its rotated
 * footprint. Boxes without a size are skipped; null when none remain.
 * @returns {{minX:number, maxX:number, minY:number, maxY:number, minZ:number, maxZ:number}|null}
 */
export function collisionBoxesUnion(boxes, rotation = 0) {
  const cos = Math.cos(rotation), sin = Math.sin(rotation);
  const absCos = Math.abs(cos), absSin = Math.abs(sin);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const { size, offset } of boxes) {
    if (!size) continue;
    const ox = offset?.x ?? 0, oy = offset?.y ?? 0, oz = offset?.z ?? 0;
    const rx = ox * cos + oz * sin;
    const rz = -ox * sin + oz * cos;
    const hw = (size.x * absCos + size.z * absSin) / 2;
    const hd = (size.x * absSin + size.z * absCos) / 2;
    minX = Math.min(minX, rx - hw); maxX = Math.max(maxX, rx + hw);
    minZ = Math.min(minZ, rz - hd); maxZ = Math.max(maxZ, rz + hd);
    minY = Math.min(minY, oy - size.y / 2); maxY = Math.max(maxY, oy + size.y / 2);
  }
  return Number.isFinite(minX) ? { minX, maxX, minY, maxY, minZ, maxZ } : null;
}

// Below this delta rotation the screw axis is ill-conditioned (cot(θ/2) → ∞) and the arc is
// indistinguishable from the chord anyway.
const SCREW_MIN_ANGLE_RAD = THREE.MathUtils.degToRad(15);

/**
 * Chasles: every rigid move is one rotation about an axis plus a slide along it, so solving that
 * screw lets a position follow the swing instead of the chord. The axis passes through
 * c = ½(t⊥ + cot(θ/2)·(n̂ × t⊥)) for delta rotation θ about unit n̂ and residual translation t.
 * @returns {{angle:number, axis:THREE.Vector3, center:THREE.Vector3, slide:number,
 *   fromRel:THREE.Vector3}|null} null when the turn is too small to define an axis
 */
export function buildScrewPath(fromPos, fromQuat, toPos, toQuat) {
  const qDelta = fromQuat.clone().invert().premultiply(toQuat).normalize();
  // Shortest path, matching the caller's own slerp — otherwise the offset winds the long way.
  if (qDelta.w < 0) qDelta.set(-qDelta.x, -qDelta.y, -qDelta.z, -qDelta.w);
  const sin = Math.hypot(qDelta.x, qDelta.y, qDelta.z);
  const angle = 2 * Math.atan2(sin, qDelta.w);
  if (angle < SCREW_MIN_ANGLE_RAD) return null;

  const axis = new THREE.Vector3(qDelta.x, qDelta.y, qDelta.z).divideScalar(sin);
  const perp = fromPos.clone().applyQuaternion(qDelta).negate().add(toPos);
  const slide = perp.dot(axis);
  perp.addScaledVector(axis, -slide);
  const center = axis.clone().cross(perp).multiplyScalar(1 / Math.tan(angle / 2))
    .add(perp).multiplyScalar(0.5);

  return { angle, axis, center, slide, fromRel: fromPos.clone().sub(center) };
}

/**
 * Free every geometry and material under `root`. Note THREE's `.clone()` shares geometry with the
 * source, so disposing a clone frees the original's buffers too.
 * @param {THREE.Object3D|null} root
 */
export function disposeMesh(root) {
  if (!root) return;
  root.traverse((child) => {
    child.geometry?.dispose();
    const material = child.material;
    if (Array.isArray(material)) material.forEach((mat) => mat.dispose());
    else material?.dispose();
  });
}

/**
 * Walk an element's mesh tree, stopping at the components reparented under it — they wear their
 * own fabric and selection — unless `includeComponents`. `fn` returning false skips that subtree.
 */
export function traverseOwnObjects(element, fn, { includeComponents = false } = {}) {
  const componentRoots = new Set(includeComponents ? null : element.attachedChildren?.map(child => child.mesh));
  const walk = (obj) => {
    if (componentRoots.has(obj) || fn(obj) === false) return;
    for (const child of obj.children) walk(child);
  };
  if (element.mesh) walk(element.mesh);
}
